import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X, Trophy, XCircle, Clock, Ban, DollarSign, Building2, FileText, Check,
} from 'lucide-react';
import { loggedMutation, apiClient } from './loggedFetch';
import logger from '../../lib/logger';

/**
 * OutcomeModal — records the final outcome of a sales play.
 *
 * Appears when user clicks "Record Outcome" on cards in the 90-day column.
 * Enables the feedback loop for win rate tracking.
 */

const OUTCOME_OPTIONS = [
  { value: 'won', label: 'Won', icon: Trophy, color: 'var(--color-success, #16a34a)' },
  { value: 'lost', label: 'Lost', icon: XCircle, color: 'var(--color-danger, #dc2626)' },
  { value: 'deferred', label: 'Deferred', icon: Clock, color: 'var(--color-warning, #f59e0b)' },
  { value: 'disqualified', label: 'Disqualified', icon: Ban, color: 'var(--color-text-muted)' },
];

function OutcomeModal({ isOpen, vendor, card, onClose, onSuccess }) {
  const queryClient = useQueryClient();
  const [outcomeStatus, setOutcomeStatus] = useState('');
  const [outcomeDate, setOutcomeDate] = useState('');
  const [actualRevenue, setActualRevenue] = useState('');
  const [outcomeNotes, setOutcomeNotes] = useState('');
  const [outcomeCompetitor, setOutcomeCompetitor] = useState('');

  // Reset form when modal opens with a card
  useEffect(() => {
    if (isOpen && card) {
      setOutcomeStatus(card.outcome_status || '');
      setOutcomeDate(card.outcome_date ? card.outcome_date.slice(0, 10) : '');
      setActualRevenue(card.actual_revenue ? String(card.actual_revenue) : '');
      setOutcomeNotes(card.outcome_notes || '');
      setOutcomeCompetitor(card.outcome_competitor || '');
    }
  }, [isOpen, card]);

  const saveMutation = useMutation({
    mutationFn: loggedMutation('OutcomeModal:save', (body) =>
      apiClient.put(
        `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards/${card?.id}/outcome`,
        body
      )
    ),
    onSuccess: (data) => {
      logger.vendorAnalysis.info('OutcomeModal:saved', {
        vendor,
        card_id: card?.id,
        outcome_status: outcomeStatus,
      });
      queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] });
      if (onSuccess) onSuccess(data);
      onClose();
    },
  });

  if (!isOpen || !card) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!outcomeStatus) return;

    saveMutation.mutate({
      outcome_status: outcomeStatus,
      outcome_date: outcomeDate || null,
      actual_revenue: actualRevenue ? parseFloat(actualRevenue) : null,
      outcome_notes: outcomeNotes || null,
      outcome_competitor: outcomeCompetitor || null,
    });
  };

  const isLost = outcomeStatus === 'lost';
  const isWon = outcomeStatus === 'won';

  return (
    <div className="om-overlay" onClick={onClose}>
      <div className="om-content" onClick={(e) => e.stopPropagation()}>
        <header className="om-header">
          <div className="om-title">
            <FileText size={18} />
            <div>
              <div className="om-title-main">Record Outcome</div>
              <div className="om-subtitle">{card.title}</div>
            </div>
          </div>
          <button className="om-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="om-body">
          {/* Outcome status selector */}
          <div className="om-field">
            <label className="om-label">Outcome</label>
            <div className="om-status-grid">
              {OUTCOME_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const isSelected = outcomeStatus === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`om-status-btn ${isSelected ? 'is-selected' : ''}`}
                    style={{
                      '--status-color': opt.color,
                      borderColor: isSelected ? opt.color : undefined,
                      background: isSelected ? `color-mix(in srgb, ${opt.color} 12%, transparent)` : undefined,
                    }}
                    onClick={() => setOutcomeStatus(opt.value)}
                  >
                    <Icon size={16} style={{ color: opt.color }} />
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Outcome date */}
          <div className="om-field">
            <label className="om-label" htmlFor="outcome-date">
              <Clock size={12} /> Outcome Date
            </label>
            <input
              id="outcome-date"
              type="date"
              className="om-input"
              value={outcomeDate}
              onChange={(e) => setOutcomeDate(e.target.value)}
            />
          </div>

          {/* Actual revenue (for wins) */}
          {isWon && (
            <div className="om-field">
              <label className="om-label" htmlFor="actual-revenue">
                <DollarSign size={12} /> Actual Revenue (optional)
              </label>
              <input
                id="actual-revenue"
                type="number"
                step="0.01"
                min="0"
                className="om-input"
                placeholder="150000.00"
                value={actualRevenue}
                onChange={(e) => setActualRevenue(e.target.value)}
              />
            </div>
          )}

          {/* Winning competitor (for losses) */}
          {isLost && (
            <div className="om-field">
              <label className="om-label" htmlFor="outcome-competitor">
                <Building2 size={12} /> Who Won?
              </label>
              <input
                id="outcome-competitor"
                type="text"
                className="om-input"
                placeholder="CrowdStrike, Zscaler, etc."
                value={outcomeCompetitor}
                onChange={(e) => setOutcomeCompetitor(e.target.value)}
              />
            </div>
          )}

          {/* Notes */}
          <div className="om-field">
            <label className="om-label" htmlFor="outcome-notes">
              <FileText size={12} /> Notes (lessons learned, context)
            </label>
            <textarea
              id="outcome-notes"
              className="om-textarea"
              placeholder="What worked? What didn't? Why did we win/lose?"
              rows={3}
              value={outcomeNotes}
              onChange={(e) => setOutcomeNotes(e.target.value)}
            />
          </div>

          {saveMutation.error && (
            <div className="om-error">
              Failed to save: {saveMutation.error.message}
            </div>
          )}
        </form>

        <footer className="om-footer">
          <button type="button" className="om-btn om-btn--secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="om-btn om-btn--primary"
            disabled={!outcomeStatus || saveMutation.isPending}
            onClick={handleSubmit}
          >
            {saveMutation.isPending ? 'Saving...' : (
              <>
                <Check size={14} /> Record Outcome
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default OutcomeModal;
